import { css } from '@emotion/react';
import { Box, Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { booleanField } from '../../../data-fields/CommonFields';
import { RendererType } from '../../../data-models/field-definition';
import { kpiConfigAtom } from '../../../services/state/KPIConfigState';
import { createFormField, createFormFromFieldsArray } from '../../../view-models/form.view-model';
import { selectedKPIChartFieldsOrgState, TSelectedKPIChartFields } from './DataDisplayState';
import { DetailsConfigActions } from './DetailsConfigActions';
import { ExitConfigBlocker } from './ExitConfigBlocker';

const panelContainer = css`
  display: grid;
  grid-template-columns: 1fr minmax(30%, 26rem);
  column-gap: 1rem;
  overflow: hidden;
`;

export function CompanySummaryKPIChartsConfig() {
  const { pushSuccessToast } = useToastMessageState();
  const [config, setConfig] = useAtom(selectedKPIChartFieldsOrgState);

  const methods = useForm<TSelectedKPIChartFields>({
    mode: 'all',
    values: {
      selectedKPIOne: config.selectedKPIOne ?? null,
      selectedKPITwo: config.selectedKPITwo ?? null,
      kpi1ShowBudget: config.kpi1ShowBudget ?? false,
      singleYAxis: config.singleYAxis ?? false,
    },
  });

  const onSave = (newValues: TSelectedKPIChartFields) => {
    setConfig(newValues);
    pushSuccessToast({
      message: 'KPI Charts configuration saved successfully',
      autoHideDuration: 1500,
    });
  };

  const onReset = async () => {
    methods.reset();
  };

  return (
    <FormProvider {...methods}>
      <Stack height='100%'>
        <ExitConfigBlocker
          onSave={methods.handleSubmit(onSave)}
          onLeave={onReset}
          editStatus={methods.formState.isDirty ? 'modified' : 'initial'}
        />
        <Stack direction='row' alignItems={'start'} columnGap='0.5rem'>
          <Typography variant='h4' mb='1rem'>
            KPI Charts
          </Typography>
        </Stack>
        <Stack direction={'row'} gap={'1rem'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant='body2' color='text.secondary'>
            Customize the selected fields for the Company Summary Chart section. To apply all changes, press
            the button &quot;Save Changes&quot;
          </Typography>
          <DetailsConfigActions
            save={{
              handler: methods.handleSubmit(onSave),
              disabled: !methods.formState.isDirty,
            }}
            reset={{ handler: onReset, disabled: !methods.formState.isDirty }}
          />
        </Stack>
        <Box margin={'1rem 0'} css={panelContainer}>
          <CompanySummaryKPIChartsForm />
        </Box>
      </Stack>
    </FormProvider>
  );
}

const CompanySummaryKPIChartsForm = () => {
  const kpiConfigs = useAtomValue(kpiConfigAtom);

  const numericalKpis = useMemo(() => {
    return kpiConfigs.filter(
      (kpi) =>
        kpi.type !== 'string' && kpi.formMeta?.renderer?.type !== RendererType.text && kpi.type !== 'datetime'
    );
  }, [kpiConfigs]);

  const form = useMemo(() => {
    const kpi1 = createFormField({
      key: 'selectedKPIOne',
      label: 'KPI 1',
      renderer: RendererType.singleSelect,
      rendererMeta: {
        values: numericalKpis.map((kpi) => ({
          displayName: kpi.displayName,
          value: kpi.id,
        })),
      },
    });
    const kpi1ShowBudget = booleanField({
      key: 'kpi1ShowBudget',
      label: 'Show Budget For KPI 1',
    });

    const kpi2 = createFormField({
      key: 'selectedKPITwo',
      label: 'KPI 2',
      renderer: RendererType.singleSelect,
      rendererMeta: {
        values: numericalKpis.map((kpi) => ({
          displayName: kpi.displayName,
          value: kpi.id,
        })),
      },
    });
    // Use a shared Y-axis for both KPIs. Best when KPIs have similar scales and
    // units — the primary KPI's formatter is applied to the shared axis labels.
    const singleYAxis = booleanField({
      key: 'singleYAxis',
      label: 'Use Shared Y-Axis For Both KPIs',
    });
    return createFormFromFieldsArray([kpi1, kpi1ShowBudget, kpi2, singleYAxis]);
  }, [numericalKpis]);

  return <FormFactoryWithStandardLayout form={form} />;
};
